import { useRef } from 'react';
import { useModalA11y } from '../../hooks/useModalA11y';
import { ModalContext, type ModalLayout } from './modalContext';

/**
 * The ONE shell of every modal (#552). No other component draws a
 * `fixed inset-0` overlay or a `role="dialog"`; `modal-standard-552.spec.ts`
 * fails when one does. The rules are in `agent-docs/code-patterns.md § Modals`.
 *
 * - The overlay has `m-0`. A modal is often a child of a `space-y-*` parent,
 *   and that rule gave the fixed overlay a bottom margin: the bottom
 *   navigation stayed bright and took a tap, and the tap left the page.
 * - The backdrop closes the modal only when the press AND the release are on
 *   it. A text selection that starts in a field and ends on the backdrop does
 *   not close the modal and lose the form.
 * - `useModalA11y` gives Esc, the focus trap, the first focus, the return of
 *   the focus and the scroll lock of the page.
 * - The context gives `onClose` to the close X of ModalHeader, and the layout
 *   to ModalHeader, ModalBody and ModalFooter.
 */

/** The widths, 1:1 with Tailwind: sm 384, md 448, lg 512, 2xl 672, 3xl 768, 5xl 1024 px. */
export type ModalWidth = 'sm' | 'md' | 'lg' | '2xl' | '3xl' | '5xl';

interface ModalBackdropProps {
  onClose: () => void;
  /** sm: a prompt or 1-3 fields. md (default): a normal form. lg: a long form
   *  or a detail view. 2xl: Plan officials. 3xl: Bout plan analysis. 5xl: the
   *  planners and the wizards. */
  maxWidth?: ModalWidth;
  /** 'form' (default): p-6, and the whole dialog scrolls. 'panel': the header
   *  and the footer stay in place, and ModalBody scrolls. Use 'panel' when
   *  the content can be taller than a phone screen. */
  layout?: ModalLayout;
  /** 'panel' only: a fixed height (90dvh), so the dialog does not change its
   *  height when its tabs or its steps change. */
  fullHeight?: boolean;
  /** The name of the dialog for a screen reader, from i18n. An e2e spec can
   *  select a dialog by it: keep a pinned name exactly. */
  ariaLabel: string;
  children: React.ReactNode;
}

const widthClass: Record<ModalWidth, string> = {
  sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', '2xl': 'max-w-2xl', '3xl': 'max-w-3xl', '5xl': 'max-w-5xl',
};

export default function ModalBackdrop({ onClose, maxWidth = 'md', layout = 'form', fullHeight = false, ariaLabel, children }: ModalBackdropProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // The press AND the release must be on the backdrop itself (not in the
  // dialog): a click event of a press on the backdrop and a release in the
  // dialog also has the backdrop as its target.
  const pressOnBackdrop = useRef(false);
  const releaseOnBackdrop = useRef(false);
  useModalA11y(dialogRef, onClose);

  const shell = layout === 'panel'
    ? `${fullHeight ? 'h-[90dvh]' : 'max-h-[90dvh]'} flex flex-col overflow-hidden`
    : 'max-h-[90dvh] overflow-y-auto overscroll-contain p-6';

  return (
    <div
      data-testid="modal"
      className="fixed inset-0 m-0 z-50 flex items-center justify-center bg-black/40 no-print"
      onMouseDown={e => { pressOnBackdrop.current = e.target === e.currentTarget; }}
      onMouseUp={e => { releaseOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={e => {
        if (pressOnBackdrop.current && releaseOnBackdrop.current && e.target === e.currentTarget) onClose();
        pressOnBackdrop.current = false;
        releaseOnBackdrop.current = false;
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={`relative bg-white rounded-2xl shadow-2xl w-full ${widthClass[maxWidth]} mx-4 ${shell} focus:outline-hidden`}
      >
        <ModalContext.Provider value={{ onClose, layout }}>
          {children}
        </ModalContext.Provider>
      </div>
    </div>
  );
}
